import { useField } from "formik";
import { Card, Flex, Text } from "@jsq/ds/core";
import { FormSwitch } from "@jsq/ds/forms";
import { SPACING } from "~/styles/variables";
import { OffState } from "./OffState";
import { OnState } from "./OnState";
import type { DataFormatValues, DataFormatVariant } from "./types";
import type { FieldDataType } from "~/graphql/supergraphTypes";

const joinPath = (prefix: string, name: string) => (prefix ? `${prefix}.${name}` : name);

/** Field off-state preview: merge field decimal override into inherited query values. */
const FieldOffState = ({
  inheritedValues,
  fieldType,
  valuesPath,
}: {
  inheritedValues?: DataFormatValues;
  fieldType?: FieldDataType;
  valuesPath: string;
}) => {
  const [{ value: overrideDecimalPlaces }] = useField<boolean>("overrideDecimalPlaces");
  const [{ value: fieldDecimalPlaces }] = useField<number>(
    joinPath(valuesPath, "decimalPlaces"),
  );

  const previewValues =
    inheritedValues && overrideDecimalPlaces
      ? { ...inheritedValues, decimalPlaces: fieldDecimalPlaces }
      : inheritedValues;

  return <OffState variant="field" inheritedValues={previewValues} fieldType={fieldType} />;
};

/**
 * Reusable data-format card. Both `QuerySettingsPanel` (query-level)
 * and `FieldSettingsPanel` (field-level) mount this.
 *
 * The component is unaware of where its values live: it reads/writes
 * Formik fields under `valuesPath` (empty string for flat forms) plus a
 * separate `toggleName` for the on/off toggle.
 */
export type DataFormatSectionProps = {
  /** Card title. Defaults to the query-level copy. */
  title?: string;
  /** Label rendered next to the card-header toggle. */
  toggleLabel: string;
  /** Formik field name (boolean) controlling on/off state. */
  toggleName: string;
  /** Formik path prefix for the format value fields. Empty string when
   * the values live at the top of the form (query-level). */
  valuesPath: string;
  /** Switches the off-state body. */
  variant: DataFormatVariant;
  /**
   * Query-level format values rendered as disabled form controls (via
   * `OffState` → `DisabledBody`) in the field-variant off-state, so the
   * user can see which inherited values would apply without editing them.
   * When omitted (e.g. query-level), the off-state falls back to the
   * "Inheriting query defaults" info banner. Only relevant when
   * `variant === "field"`.
   */
  inheritedValues?: DataFormatValues;
  /**
   * When provided, limits the visible fields in the on-state to those
   * relevant for this field type. When omitted (query-level), all fields
   * are shown.
   */
  fieldType?: FieldDataType;
  isReadOnly?: boolean;
};

export const DataFormatSection = ({
  title = "Default display format",
  toggleLabel,
  toggleName,
  valuesPath,
  variant,
  inheritedValues,
  fieldType,
  isReadOnly = false,
}: DataFormatSectionProps) => {
  // Read the toggle value to decide which body to render. The DS
  // FormSwitch handles the read/write wiring; this hook is only here to
  // branch the rendered children.
  const [{ value: isToggleOn }] = useField<boolean>({ name: toggleName, type: "checkbox" });

  return (
    <Card
      isFullWidth
      title={title}
      actionComponent={
        <Flex alignItems="center" gap={SPACING.SMALL}>
          <Text variant="regular-md">{toggleLabel}</Text>
          <FormSwitch
            name={toggleName}
            id={`data-format-toggle-${toggleName}`}
            size="medium"
            data-testid="data-format-toggle"
            disabled={isReadOnly}
          />
        </Flex>
      }
    >
      {isToggleOn ? (
        <OnState valuesPath={valuesPath} fieldType={fieldType} isReadOnly={isReadOnly} />
      ) : variant === "field" ? (
        <FieldOffState
          inheritedValues={inheritedValues}
          fieldType={fieldType}
          valuesPath={valuesPath}
        />
      ) : (
        <OffState variant={variant} inheritedValues={inheritedValues} fieldType={fieldType} />
      )}
    </Card>
  );
};
